import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import { X, Eye, ClipboardList, Upload, CheckCircle, AlertCircle, Loader2, Lock, Unlock } from 'lucide-react'
import { useControlCard } from '@/hooks'
import { supabase } from '@/lib/supabase/client'
import { STM_LOGO, CERT_LOGO } from '@/assets/control card'
import { hasAdminAccess } from '@/utils/permissions'
import { uploadDocument } from '@/services/document-upload.service'
import {
  addYearsToIsoDate,
  buildEtReport,
  fetchEtCompany,
  profileHasEt,
  renewNdtDates,
  saveEtReport,
  updateNdtDatesForEquipment,
  type EtCompany,
} from '@/services/month48Inspection.service'
import { AppBarTitle } from '@/components/layout/pageHeader'
import { mergePdfBlobs } from '@/utils/mergePdfBlobs'
import {
  STMIDBEquipmentService,
  STMIDB_WLL_COMPANY_ID,
  getSTMIDBUploadCode,
  mapWLLDocumentTypeToSTMIDB,
} from '@/services/api/stmidbEquipmentService'
import { getSTMIDBNdtCategoryCode } from '@/utils/ndtEquipmentReport'
import { isSlingCategory } from '@/utils/categoryFamily'
import { completeJobAssignmentsForEquipment, type JobWorkType } from '@/services/jobs.service'

const CONTROL_CARD_WORK_TYPES: JobWorkType[] = ['annual', '48_months']

const PdfPreview = lazy(() => import('@/components/ui/PdfPreview'))

interface Equipment {
  id: string
  name: string
  asset_number: string
  serial_number: string | null
  /** STMIDB equipment_code for SLB rows; keys the STMIDB upload path. */
  owner_id?: string | null
  status: string
  categoryCode?: string | null
  next_ndt_date?: string | null
}

interface InspectionModalProps {
  isOpen: boolean
  onClose: () => void
  equipment: Equipment
  /**
   * SLB equipment lives in STMIDB: dates, NDT and PDFs are written through
   * STMIDBEquipmentService instead of equipment_v2 / WLL storage.
   */
  isSTMIDBEquipment?: boolean
  /** Order number of the job the inspection was started from; overrides the suggestion. */
  defaultOrderNo?: string | null
}

interface SameDayEquipment {
  id: string
  name: string
  serial_number: string | null
  asset_number: string
}

interface InspectionFormData {
  orderNo: string
  performedDate: string
  controlledAgainst: string
  status: string
  remarks: string
  performedById: string
  ordreansvarligId: string
}

interface Supervisor {
  id: string
  full_name: string
  signature: string | null
  role: string
  hasEt?: boolean
}

const STATUS_OPTIONS = [
  { value: 'OK',  label: 'OK – Godkjent',       color: 'bg-[#00B050]' },
  { value: 'C',   label: 'C – Kommentar',        color: 'bg-[#5CC88C]' },
  { value: 'MO',  label: 'MO – Memorandum',      color: 'bg-[#5CC88C]' },
  { value: 'RC',  label: 'RC – Anbefaling',      color: 'bg-[#FF3300]' },
  { value: 'NC',  label: 'NC – Ikke godkjent',   color: 'bg-[#CC0000]' },
]

const CONTROLLED_OPTIONS = [
  {
    id: 'land',
    label: 'Verksted/land',
    value: 'Forskrift Nr 1357, 1360',
  },
  {
    id: 'offshore',
    label: 'Offshore',
    value: 'Forskrift Nr 1357, 1360 & NORSOK R-003',
  },
] as const

const getTodayDate = () => new Date().toISOString().split('T')[0]

const hasDisplayName = (name: string | null | undefined) => !!name?.trim()

const fieldLabel = (text: string) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1.5">{text}</p>
)

const selectCls =
  'w-full px-4 py-2.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800 bg-white transition-colors'

const InspectionModal = ({ isOpen, onClose, equipment, isSTMIDBEquipment = false, defaultOrderNo }: InspectionModalProps) => {
  const [formData, setFormData] = useState<InspectionFormData>({
    orderNo: '',
    performedDate: getTodayDate(),
    controlledAgainst: 'Forskrift Nr 1357, 1360',
    status: 'OK',
    remarks: '',
    performedById: '',
    ordreansvarligId: '',
  })

  const [uploadedImage, setUploadedImage]     = useState<string | null>(null)
  const [userProfile, setUserProfile]         = useState<any>(null)
  const [inspectors, setInspectors]           = useState<Supervisor[]>([])
  const [supervisors, setSupervisors]         = useState<Supervisor[]>([])
  const [loadingInspectors, setLoadingInspectors] = useState(false)
  const [loadingSupervisors, setLoadingSupervisors] = useState(false)
  const [showPreview, setShowPreview]         = useState(false)
  const [previewUrl, setPreviewUrl]           = useState<string | null>(null)
  const [etPreviewUrl, setEtPreviewUrl]       = useState<string | null>(null)
  const [previewTab, setPreviewTab]           = useState<'control_card' | 'et'>('control_card')
  const [toast, setToast]                     = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [orderNoLocked, setOrderNoLocked]     = useState(false)
  const [kontrolltype, setKontrolltype]       = useState<'annual' | 'month48'>('annual')
  const [isPublishing, setIsPublishing]       = useState(false)
  /** NDT expires before the next annual inspection: MO is preselected, not enforced. */
  const [ndtMoDefault, setNdtMoDefault]       = useState(false)
  const ndtMoRemarkRef = useRef('')
  const [step, setStep] = useState<'loading' | 'liste' | 'form'>('loading')
  const [sameDayEquipment, setSameDayEquipment] = useState<SameDayEquipment[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [publishProgress, setPublishProgress] = useState<string | null>(null)
  const [previewProgress, setPreviewProgress] = useState<string | null>(null)
  const [previewCount, setPreviewCount] = useState(1)

  const { preview, finalize, isGenerating, error } = useControlCard()
  const month48 = kontrolltype === 'month48'

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }, [])

  const canChoosePerformer = hasAdminAccess(userProfile)
  const selectedPerformer = inspectors.find(s => s.id === formData.performedById)
    || (userProfile?.id === formData.performedById ? userProfile : null)
  const selectedOrdreansvarlig = supervisors.find(s => s.id === formData.ordreansvarligId)
  const canChoose48 = profileHasEt(selectedPerformer)
  const performerOptions = [
    ...(userProfile?.id && hasDisplayName(userProfile.full_name) && !inspectors.some(i => i.id === userProfile.id)
      ? [{ id: userProfile.id, full_name: userProfile.full_name, signature: userProfile.signature, role: userProfile.role, hasEt: profileHasEt(userProfile) }]
      : []),
    ...inspectors,
  ].filter(p => hasDisplayName(p.full_name))

  useEffect(() => {
    if (kontrolltype !== 'month48' || canChoose48) return
    setKontrolltype('annual')
    if (ndtMoDefault) {
      setFormData(prev => ({ ...prev, status: 'MO', remarks: ndtMoRemarkRef.current }))
    }
  }, [canChoose48, kontrolltype, ndtMoDefault])

  useEffect(() => {
    if (!isOpen) return

    setFormData({
      orderNo: '',
      performedDate: getTodayDate(),
      controlledAgainst: CONTROLLED_OPTIONS[0].value,
      status: 'OK',
      remarks: '',
      performedById: '',
      ordreansvarligId: '',
    })
    setNdtMoDefault(false)
    ndtMoRemarkRef.current = ''
    setUploadedImage(null)
    setKontrolltype('annual')
    setEtPreviewUrl(null)
    setPreviewTab('control_card')
    setStep('loading')
    setSameDayEquipment([])
    setSelectedIds(new Set([equipment.id]))
    setPublishProgress(null)
    setPreviewProgress(null)
    setPreviewCount(1)
    fetchUserProfile()
    fetchInspectors()
    fetchSupervisors()
    fetchOrderNoSuggestion()
    fetchSameDayEquipment()

    // Async NDT check — inline IIFE to avoid hoisting issues
    ;(async () => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const nextYear = new Date(today)
      nextYear.setFullYear(nextYear.getFullYear() + 1)

      const fmtIso = (iso: string) => {
        const [y, m, d] = iso.split('-')
        return `${d}.${m}.${y}`
      }

      const plusFourYears = (dateStr: string): Date | null => {
        try {
          const d = new Date(dateStr)
          if (isNaN(d.getTime())) return null
          d.setFullYear(d.getFullYear() + 4)
          return d
        } catch { return null }
      }

      const candidates: { date: Date; source: 'unit' | 'sling' }[] = []

      if (isSTMIDBEquipment) {
        // STMIDB's last_ndt_date is kept in sync with the NDT Dato spec.
        const [main, children] = await Promise.all([
          STMIDBEquipmentService.getEquipmentById(equipment.id).catch(() => null),
          STMIDBEquipmentService.getSubEquipment(equipment.id),
        ])
        const mainDate = main?.last_ndt_date ? plusFourYears(main.last_ndt_date) : null
        if (mainDate) candidates.push({ date: mainDate, source: 'unit' })
        for (const child of children) {
          if (!isSlingCategory(getSTMIDBNdtCategoryCode(child.category, child.equipment_type))) continue
          const d = child.last_ndt_date ? plusFourYears(child.last_ndt_date) : null
          if (d) candidates.push({ date: d, source: 'sling' })
        }
      } else {
        // Main equipment: read last NDT from specs, compute next = last + 4 years
        const { data: mainSpec } = await supabase
          .from('equipment_specifications_v2')
          .select('value_date, value_text')
          .eq('equipment_id', equipment.id)
          .in('spec_key', ['ndt_date', 'last_ndt_date'])
          .limit(1)
          .maybeSingle()

        const mainLastNdt = (mainSpec as any)?.value_date || mainSpec?.value_text || null
        if (mainLastNdt) {
          const d = plusFourYears(mainLastNdt)
          if (d) candidates.push({ date: d, source: 'unit' })
        }

        // Slings: get sub-equipment ids filtered to R1/R3
        const { data: slingRels } = await supabase
          .from('equipment_relationship_v2')
          .select(`
            sub_equipment_id,
            sub_equipment:equipment_v2!equipment_relationship_v2_sub_equipment_id_fkey(
              categories_v2(code)
            )
          `)
          .eq('main_equipment_id', equipment.id)

        const slingIds = (slingRels || [])
          .filter(rel => {
            const sub = (Array.isArray(rel.sub_equipment) ? rel.sub_equipment[0] : rel.sub_equipment) as any
            const cat = Array.isArray(sub?.categories_v2) ? sub.categories_v2[0] : sub?.categories_v2
            return isSlingCategory(cat?.code)
          })
          .map(rel => rel.sub_equipment_id)

        if (slingIds.length > 0) {
          const { data: slingSpecs } = await supabase
            .from('equipment_specifications_v2')
            .select('value_date, value_text')
            .in('equipment_id', slingIds)
            .in('spec_key', ['ndt_date', 'last_ndt_date'])

          for (const spec of slingSpecs || []) {
            const lastNdt = (spec as any).value_date || spec.value_text
            if (lastNdt) {
              const d = plusFourYears(lastNdt)
              if (d) candidates.push({ date: d, source: 'sling' })
            }
          }
        }
      }

      const expiring = candidates
        .filter(c => c.date < nextYear)
        .sort((a, b) => a.date.getTime() - b.date.getTime())

      const earliest = expiring[0]

      if (earliest) {
        const iso = earliest.date.toISOString().split('T')[0]
        const hasUnit  = expiring.some(c => c.source === 'unit')
        const hasSling = expiring.some(c => c.source === 'sling')
        const suffix = hasUnit && hasSling ? '' : hasUnit ? ', unit' : ', sling'
        const remark = `MO - Gyldig til ${fmtIso(iso)} grunnet NDT-dato${suffix}`
        ndtMoRemarkRef.current = remark
        setFormData(prev => ({
          ...prev,
          status: 'MO',
          remarks: remark,
        }))
        setNdtMoDefault(true)
      }
    })()
  }, [isOpen, equipment.id, equipment.next_ndt_date])

  const fetchSameDayEquipment = async () => {
    // Same-day batch publishing is for equipment created in this app (equipment_v2).
    if (isSTMIDBEquipment) {
      setStep('form')
      return
    }
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setStep('form')
        return
      }

      const { data: current } = await supabase
        .from('equipment_v2')
        .select('id, name, serial_number, asset_number, company_id, created_by, created_at')
        .eq('id', equipment.id)
        .single()

      if (!current?.company_id || !current.created_at) {
        setStep('form')
        return
      }

      const startOfToday = new Date()
      startOfToday.setHours(0, 0, 0, 0)
      if (new Date(current.created_at) < startOfToday) {
        setStep('form')
        return
      }

      const { data } = await supabase
        .from('equipment_v2')
        .select('id, name, serial_number, asset_number, created_at')
        .eq('company_id', current.company_id)
        .eq('created_by', user.id)
        .gte('created_at', startOfToday.toISOString())
        .order('created_at', { ascending: false })

      const items: SameDayEquipment[] = (data || []).map(row => ({
        id: row.id,
        name: row.name || '',
        serial_number: row.serial_number,
        asset_number: row.asset_number || '',
      }))

      if (!items.some(item => item.id === equipment.id)) {
        items.unshift({
          id: current.id,
          name: current.name || equipment.name,
          serial_number: current.serial_number ?? equipment.serial_number,
          asset_number: current.asset_number || equipment.asset_number,
        })
      }

      if (items.length <= 1) {
        setSameDayEquipment(items)
        setSelectedIds(new Set([equipment.id]))
        setStep('form')
        return
      }

      const currentFirst = [
        items.find(item => item.id === equipment.id)!,
        ...items.filter(item => item.id !== equipment.id),
      ]
      setSameDayEquipment(currentFirst)
      setSelectedIds(new Set(currentFirst.map(item => item.id)))
      setStep('liste')
    } catch (err) {
      console.error('Failed to load same-day equipment:', err)
      setStep('form')
    }
  }

  const toggleSelected = (id: string) => {
    if (id === equipment.id) return
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      next.add(equipment.id)
      return next
    })
  }

  const getSelectedTargets = (): SameDayEquipment[] => {
    const currentItem: SameDayEquipment = {
      id: equipment.id,
      name: equipment.name,
      serial_number: equipment.serial_number,
      asset_number: equipment.asset_number,
    }
    const extras = sameDayEquipment.filter(item => item.id !== equipment.id && selectedIds.has(item.id))
    return [currentItem, ...extras]
  }

  const KNOWN_COMPANIES = [
    'air liquide', 'beerenberg', 'technipfmc', 'cameron', 'df rental',
    'enhanced drilling', 'fourphase', 'oeg', 'ses energy', 'slb',
  ]

  const fetchOrderNoSuggestion = async () => {
    const jobOrderNo = defaultOrderNo?.trim()
    if (jobOrderNo) {
      setFormData(prev => ({ ...prev, orderNo: jobOrderNo }))
      setOrderNoLocked(false)
      return
    }
    try {
      const company = isSTMIDBEquipment
        ? (await supabase
            .from('companies')
            .select('name, report_code')
            .eq('id', STMIDB_WLL_COMPANY_ID)
            .single()).data as any
        : (await supabase
            .from('equipment_v2')
            .select('company_id, companies(name, report_code)')
            .eq('id', equipment.id)
            .single()).data?.companies as any

      const name      = (company?.name as string | null) ?? ''
      const shortCode = (company?.report_code as string | null | undefined) ?? null

      const now    = new Date()
      const yy     = String(now.getFullYear()).slice(2)
      const mm     = String(now.getMonth() + 1).padStart(2, '0')
      const prefix = `I${yy}${mm}`

      const isKnown = KNOWN_COMPANIES.some(k => name.toLowerCase().includes(k))

      if (isKnown && shortCode) {
        setFormData(prev => ({ ...prev, orderNo: `${prefix}${shortCode}` }))
        setOrderNoLocked(true)
      } else {
        setFormData(prev => ({ ...prev, orderNo: prefix }))
        setOrderNoLocked(false)
      }
    } catch {
      const now    = new Date()
      const yy     = String(now.getFullYear()).slice(2)
      const mm     = String(now.getMonth() + 1).padStart(2, '0')
      setFormData(prev => ({ ...prev, orderNo: `I${yy}${mm}` }))
      setOrderNoLocked(false)
    }
  }

  const fetchUserProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, full_name, signature, role, supervisor, et, et_level')
        .eq('id', user.id)
        .single()
      if (profile) {
        setUserProfile(profile)
        setFormData(prev => ({
          ...prev,
          performedById: profile.id,
          ...(hasAdminAccess(profile) ? { ordreansvarligId: user.id } : {}),
        }))
      }
    }
  }

  const fetchInspectors = async () => {
    setLoadingInspectors(true)
    try {
      const { data: inspectorUsers, error: inspectorError } = await supabase
        .from('profiles')
        .select('id, full_name, signature, role, supervisor, et, et_level')
        .eq('role', 'inspector')
        .order('full_name')

      const { data: adminUsers, error: adminError } = await supabase
        .from('profiles')
        .select('id, full_name, signature, role, supervisor, et, et_level')
        .eq('role', 'admin')
        .order('full_name')

      if (inspectorError) console.error('Failed to fetch inspectors:', inspectorError)
      if (adminError) console.error('Failed to fetch admins for performer list:', adminError)

      const combined = [...(inspectorUsers || []), ...(adminUsers || [])]
      const unique = combined.reduce((acc, cur) => {
        if (!acc.find(i => i.id === cur.id)) acc.push(cur)
        return acc
      }, [] as typeof combined)

      unique.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
      setInspectors(unique
        .filter(u => hasDisplayName(u.full_name))
        .map(u => ({
          id: u.id,
          full_name: u.full_name.trim(),
          signature: u.signature,
          role: u.role === 'admin' ? 'admin' : u.supervisor ? 'supervisor' : 'inspector',
          hasEt: profileHasEt(u),
        })))
    } catch (err) {
      console.error('Failed to fetch inspectors:', err)
    } finally {
      setLoadingInspectors(false)
    }
  }

  const fetchSupervisors = async () => {
    setLoadingSupervisors(true)
    try {
      const { data: admins }      = await supabase.from('profiles').select('id, full_name, signature, role, supervisor').eq('role', 'admin').order('full_name')
      const { data: supervisorUsers } = await supabase.from('profiles').select('id, full_name, signature, role, supervisor').eq('supervisor', true).order('full_name')

      const combined = [...(admins || []), ...(supervisorUsers || [])]
      const unique = combined.reduce((acc, cur) => {
        if (!acc.find(i => i.id === cur.id)) acc.push(cur)
        return acc
      }, [] as typeof combined)

      unique.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
      setSupervisors(unique
        .filter(u => hasDisplayName(u.full_name))
        .map(u => ({
          id: u.id,
          full_name: u.full_name.trim(),
          signature: u.signature,
          role: u.role === 'admin' ? 'admin' : 'supervisor',
        })))
    } catch (err) {
      console.error('Failed to fetch supervisors:', err)
    } finally {
      setLoadingSupervisors(false)
    }
  }

  const set = (field: keyof InspectionFormData, value: string) =>
    setFormData(prev => ({ ...prev, [field]: value }))

  // The generated NDT remark belongs to MO; an untouched one follows the status.
  const selectStatus = (status: string) =>
    setFormData(prev => {
      const ndtRemark = ndtMoRemarkRef.current
      if (!ndtRemark) return { ...prev, status }
      if (status === 'MO' && !prev.remarks.trim()) return { ...prev, status, remarks: ndtRemark }
      if (status !== 'MO' && prev.remarks === ndtRemark) return { ...prev, status, remarks: '' }
      return { ...prev, status }
    })

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { showToast('Velg en bildefil (JPG, PNG, osv.)', 'error'); return }
    if (file.size > 10 * 1024 * 1024)   { showToast('Bildet er for stort. Maks 10 MB.', 'error'); return }
    const reader = new FileReader()
    reader.onload = ev => setUploadedImage(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const buildInspectionPayload = () => ({
    jobType: month48 ? '48 måneder' : 'Periodisk kontroll',
    performedDate: formData.performedDate,
    performedBy: selectedPerformer?.full_name || userProfile?.full_name || 'Unknown',
    responsiblePerson: selectedOrdreansvarlig?.full_name || userProfile?.full_name || 'Unknown',
    controlledAgainst: formData.controlledAgainst,
    status: formData.status,
    remarks: formData.remarks || undefined,
    orderNo: formData.orderNo.trim() || undefined,
    performedBySignature: selectedPerformer?.signature || userProfile?.signature || undefined,
    responsiblePersonSignature: selectedOrdreansvarlig?.signature || userProfile?.signature || undefined,
  })

  const fetchCompany = () => fetchEtCompany(equipment.id, isSTMIDBEquipment)

  const applyNdtDateUpdates = () => renewNdtDates(equipment.id, formData.performedDate, isSTMIDBEquipment)

  const etReportInput = (company: EtCompany) => ({
    equipment,
    isSTMIDB: isSTMIDBEquipment,
    company,
    orderNo: formData.orderNo,
    performedDate: formData.performedDate,
    status: formData.status,
    remarks: formData.remarks,
    issuedBy: selectedPerformer?.full_name || userProfile?.full_name || 'Unknown',
    verifiedBy: selectedOrdreansvarlig?.full_name,
    attachedImages: uploadedImage ? [uploadedImage] : [],
  })

  const buildETPdf = (company: EtCompany) => buildEtReport(etReportInput(company))

  const handlePreview = async () => {
    if (!userProfile)              { showToast('Brukerprofil ikke lastet. Prøv igjen.', 'error'); return }
    if (!formData.performedById)   { showToast('Velg hvem som utførte kontrollen.', 'error'); return }
    if (!formData.ordreansvarligId){ showToast('Velg en ordreansvarlig.', 'error'); return }
    if (month48 && !canChoose48) {
      showToast('48-måneders kontroll krever ET-kvalifikasjon for utførende inspektør.', 'error')
      return
    }

    const targets = getSelectedTargets()
    const inspection = buildInspectionPayload()
    const cardOptions = {
      attachmentImage: uploadedImage || undefined,
      ...(month48 ? { ndtDateOverride: formData.performedDate } : {}),
    }

    setPreviewProgress(targets.length > 1 ? `Genererer 1 av ${targets.length}...` : null)
    const blobs: Blob[] = []

    for (let i = 0; i < targets.length; i++) {
      const item = targets[i]
      if (targets.length > 1) setPreviewProgress(`Genererer ${i + 1} av ${targets.length}...`)
      const result = await preview({
        equipmentId: item.id,
        inspection,
        logos: { company: STM_LOGO, certification: CERT_LOGO },
        options: cardOptions,
      })
      if (!result.success || !result.blob) {
        if (i === 0) {
          setPreviewProgress(null)
          if (error) showToast(`Forhåndsvisning feilet: ${error}`, 'error')
          return
        }
        showToast(`Hoppet over ${item.serial_number || item.asset_number}`, 'error')
        continue
      }
      blobs.push(result.blob)
    }

    if (blobs.length === 0) {
      setPreviewProgress(null)
      showToast('Forhåndsvisning feilet', 'error')
      return
    }

    try {
      const merged = await mergePdfBlobs(blobs)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      if (etPreviewUrl) URL.revokeObjectURL(etPreviewUrl)
      setPreviewUrl(URL.createObjectURL(merged))
      setEtPreviewUrl(null)
      setPreviewTab('control_card')
      setPreviewCount(blobs.length)
      setShowPreview(true)
    } catch (mergeErr) {
      console.error('Preview merge failed:', mergeErr)
      showToast('Kunne ikke slå sammen forhåndsvisning', 'error')
      setPreviewProgress(null)
      return
    }

    setPreviewProgress(null)

    if (month48) {
      try {
        const company = await fetchCompany()
        if (company) {
          const { pdfBlob } = await buildETPdf(company)
          setEtPreviewUrl(URL.createObjectURL(pdfBlob))
        }
      } catch (etErr) {
        console.error('ET preview failed:', etErr)
        showToast('Kontrollkort vises, men ET-forhåndsvisning feilet', 'error')
      }
    }
  }

  const generateETReport = (company: EtCompany): Promise<Blob | null> =>
    saveEtReport({ ...etReportInput(company), approvedById: formData.ordreansvarligId || null })

  const handlePublish = async () => {
    if (!userProfile)               { showToast('Brukerprofil ikke lastet. Prøv igjen.', 'error'); return }
    if (!formData.performedById)    { showToast('Velg hvem som utførte kontrollen.', 'error'); return }
    if (!formData.ordreansvarligId) { showToast('Velg en ordreansvarlig.', 'error'); return }
    if (month48 && !canChoose48) {
      showToast('48-måneders kontroll krever ET-kvalifikasjon for utførende inspektør.', 'error')
      return
    }
    if (formData.status !== 'OK' && !formData.remarks.trim()) {
      showToast('Legg til merknad for ikke-OK status.', 'error')
      return
    }

    const currentItem: SameDayEquipment = {
      id: equipment.id,
      name: equipment.name,
      serial_number: equipment.serial_number,
      asset_number: equipment.asset_number,
    }
    const extraItems = formData.status === 'OK'
      ? getSelectedTargets().filter(item => item.id !== equipment.id)
      : []
    const targets = [currentItem, ...extraItems]

    setIsPublishing(true)
    setPublishProgress(targets.length > 1 ? `Publiserer 1 av ${targets.length}...` : null)

    if (month48) {
      await applyNdtDateUpdates()
      await Promise.all(extraItems.map(item => updateNdtDatesForEquipment(item.id, formData.performedDate, isSTMIDBEquipment)))
    }

    const inspection = buildInspectionPayload()
    const cardOptions = {
      attachmentImage: uploadedImage || undefined,
      ...(month48 ? { ndtDateOverride: formData.performedDate } : {}),
    }

    const result = await finalize({
      equipmentId: equipment.id,
      inspection,
      logos: { company: STM_LOGO, certification: CERT_LOGO },
      options: cardOptions,
    })

    if (result.success && result.blob) {
      try {
        const company = await fetchCompany()
        const companyName  = company?.name || 'unknown'
        const nextDate = addYearsToIsoDate(formData.performedDate, 1)
        const equipmentUpdate: Record<string, string> = {
          last_inspection_date: formData.performedDate,
          next_inspection_date: nextDate,
          inspection_status: formData.status,
          ...(formData.orderNo.trim() && { order_no: formData.orderNo.trim() }),
        }
        const cardResult = { status: formData.status, remarks: formData.remarks.trim() || null }

        const uploadCard = async (item: SameDayEquipment, blob: Blob, reportNumber: string) => {
          const serialNumber = item.serial_number || item.asset_number || 'unknown'
          const pdfFile = new File([blob], `${reportNumber}.pdf`, { type: 'application/pdf' })

          if (isSTMIDBEquipment) {
            // STMIDB stores the inspection code in `status` (aliased as
            // equipment_status on read).
            await STMIDBEquipmentService.uploadDocument({
              equipmentId: item.id,
              equipmentCode: getSTMIDBUploadCode({ ...item, owner_id: equipment.owner_id }),
              documentType: mapWLLDocumentTypeToSTMIDB('control_card'),
              file: pdfFile,
            })
            await STMIDBEquipmentService.updateEquipment(item.id, {
              last_inspection_date: formData.performedDate,
              next_inspection_date: nextDate,
              status: formData.status,
            })
            void completeJobAssignmentsForEquipment(item.id, CONTROL_CARD_WORK_TYPES, cardResult)
            return true
          }

          const uploadResult = await uploadDocument({
            file: pdfFile,
            customerName: companyName,
            serialNumber,
            equipmentId: item.id,
            documentType: 'control_card',
            inspectionStatus: formData.status,
          })
          await supabase.from('equipment_v2').update(equipmentUpdate).eq('id', item.id)
          if (uploadResult.success) void completeJobAssignmentsForEquipment(item.id, CONTROL_CARD_WORK_TYPES, cardResult)
          return uploadResult.success
        }

        const primaryReportNumber = result.reportNumber || `STMI-${formData.performedDate}`
        const primaryUploaded = await uploadCard(currentItem, result.blob, primaryReportNumber)
        if (!primaryUploaded) {
          showToast('PDF generert, men opplasting feilet', 'error')
        }

        let extraFailed = 0
        for (let i = 0; i < extraItems.length; i++) {
          const item = extraItems[i]
          setPublishProgress(`Publiserer ${i + 2} av ${targets.length}...`)
          const extraResult = await finalize({
            equipmentId: item.id,
            inspection,
            logos: { company: STM_LOGO, certification: CERT_LOGO },
            options: cardOptions,
          })
          if (!extraResult.success || !extraResult.blob) {
            extraFailed += 1
            continue
          }
          const extraReportNumber = extraResult.reportNumber || primaryReportNumber
          const uploaded = await uploadCard(item, extraResult.blob, extraReportNumber)
          if (!uploaded) extraFailed += 1
        }

        let etBlob: Blob | null = null
        let etFailed = false
        if (month48 && company) {
          try {
            etBlob = await generateETReport(company)
          } catch (etErr) {
            console.error('ET report generation failed:', etErr)
            etFailed = true
            showToast('Kontrollkort publisert, men ET-rapport feilet', 'error')
          }
        }

        if (extraFailed > 0) {
          showToast(
            `Publisert ${targets.length - extraFailed} av ${targets.length} kontrollkort`,
            'error',
          )
        } else if (!etFailed) {
          const cardCount = targets.length
          showToast(
            month48 && etBlob
              ? (cardCount > 1 ? `${cardCount} kontrollkort og ET-rapport publisert` : 'Kontrollkort og ET-rapport publisert')
              : (cardCount > 1 ? `${cardCount} kontrollkort publisert` : 'Kontrollkort publisert'),
            'success',
          )
        }
        setTimeout(() => onClose(), 1800)
      } catch (err) {
        showToast(`Opplasting feilet: ${err instanceof Error ? err.message : 'Ukjent feil'}`, 'error')
      } finally {
        setIsPublishing(false)
        setPublishProgress(null)
      }
    } else {
      setIsPublishing(false)
      setPublishProgress(null)
      if (error) showToast(`Publisering feilet: ${error}`, 'error')
    }
  }

  const handleClosePreview = () => {
    setShowPreview(false)
    setPreviewTab('control_card')
    setPreviewCount(1)
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null) }
    if (etPreviewUrl) { URL.revokeObjectURL(etPreviewUrl); setEtPreviewUrl(null) }
  }

  if (!isOpen) return null

  const orderNoValid = /^I\d{4}\w{3,}$/.test(formData.orderNo.trim())
  const controlledAgainstValid = !!formData.controlledAgainst
  const canPublish = !isGenerating && !isPublishing && orderNoValid && !!formData.performedDate && !!formData.performedById && !!formData.ordreansvarligId &&
    controlledAgainstValid && (formData.status === 'OK' || formData.remarks.trim().length > 0)
  const selectedCount = selectedIds.size
  const bulkCount = formData.status === 'OK' ? selectedCount : 1
  const cameFromListe = sameDayEquipment.length > 1

  return (
    <>
      {/* ── Toast ──────────────────────────────────────────── */}
      {toast && (
        <div className={`fixed top-5 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium ${
          toast.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {toast.type === 'success'
            ? <CheckCircle size={15} className="text-emerald-600 shrink-0" />
            : <AlertCircle size={15} className="text-red-500 shrink-0" />}
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-1 opacity-50 hover:opacity-100 transition-opacity">
            <X size={13} />
          </button>
        </div>
      )}

      {/* ── Backdrop ───────────────────────────────────────── */}
      <div
        className="fixed right-0 bottom-0 z-[45] flex flex-col bg-white"
        style={{ top: 'var(--topnav-height)', left: showPreview ? 0 : 'var(--rail-width)' }}
      >
        <AppBarTitle
          title={step === 'liste' ? 'Inspeksjonsliste' : 'Ny inspeksjon'}
          subtitle={
            step === 'liste'
              ? `${sameDayEquipment.length} utstyr opprettet i dag`
              : bulkCount > 1
                ? `${bulkCount} utstyr`
                : [equipment.serial_number || equipment.asset_number, equipment.name].filter(Boolean).join(' · ')
          }
          onBack={step === 'form' && cameFromListe ? () => setStep('liste') : onClose}
          hideAppHeader={showPreview}
        />
        <div className="bg-white w-full h-full overflow-hidden flex flex-col">

          {step === 'loading' && (
            <div className="flex-1 flex items-center justify-center gap-3">
              <Loader2 size={20} className="animate-spin text-[#1E3A5F]" />
              <span className="text-sm text-slate-500">Laster...</span>
            </div>
          )}

          {step === 'liste' && (
            <>
              <div className="flex-1 overflow-y-auto px-5 py-5">
                <p className="text-sm text-slate-500 mb-4">
                  Velg utstyr som skal få samme kontroll. Kontrollkort genereres for alle valgte når status er OK.
                </p>
                <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
                  {sameDayEquipment.map(item => {
                    const checked = selectedIds.has(item.id)
                    const isCurrent = item.id === equipment.id
                    return (
                      <label
                        key={item.id}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                          isCurrent ? 'bg-slate-50' : 'hover:bg-slate-50 cursor-pointer'
                        }`}
                        style={{ minHeight: 56 }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isCurrent}
                          onChange={() => toggleSelected(item.id)}
                          className="w-5 h-5 rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F] shrink-0 disabled:opacity-60"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {item.serial_number || item.asset_number || 'Uten SN'}
                          </p>
                          <p className="text-xs text-slate-400 truncate">
                            {item.name || 'Uten navn'}
                            {isCurrent ? ' · Dette utstyret' : ''}
                          </p>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>
              <div className="border-t border-slate-200 px-5 py-4 bg-slate-50 shrink-0">
                <button
                  type="button"
                  onClick={() => setStep('form')}
                  className="w-full flex items-center justify-center gap-2 px-4 bg-[#1E3A5F] text-white text-sm font-semibold rounded-md hover:bg-[#152C4A] transition-colors shadow-sm"
                  style={{ minHeight: 48 }}
                >
                  <ClipboardList size={16} />
                  Ny inspeksjon · {selectedCount} utstyr
                </button>
              </div>
            </>
          )}

          {step === 'form' && (
          <>

          {/* ── Scrollable content ─────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

            {/* Kontrolltype */}
            <div>
              {fieldLabel('Kontrolltype')}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setKontrolltype('annual')
                    if (ndtMoDefault && month48) {
                      setFormData(prev => ({ ...prev, status: 'MO', remarks: ndtMoRemarkRef.current }))
                    }
                  }}
                  className={`flex-1 px-4 py-2.5 border rounded-md text-sm font-semibold transition-colors ${
                    kontrolltype === 'annual'
                      ? 'border-[#1E3A5F] bg-[#1E3A5F] text-white'
                      : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  style={{ minHeight: 44 }}
                >
                  Årlig kontroll
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!canChoose48) return
                    setKontrolltype('month48')
                    // 48-month inspection renews NDT — unlock status
                    setFormData(prev => ({ ...prev, status: 'OK', remarks: prev.remarks.startsWith('MO - Gyldig til') ? '' : prev.remarks }))
                  }}
                  disabled={!canChoose48}
                  title={!canChoose48 ? 'Krever ET-kvalifikasjon for utførende inspektør' : undefined}
                  className={`flex-1 px-4 py-2.5 border rounded-md text-sm font-semibold transition-colors ${
                    !canChoose48
                      ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
                      : kontrolltype === 'month48'
                        ? 'border-[#1E3A5F] bg-[#1E3A5F] text-white'
                        : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  style={{ minHeight: 44 }}
                >
                  48-måneder
                </button>
              </div>
              {month48 && (
                <p className="mt-1.5 text-xs text-slate-400">
                  Inkluderer årlig kontroll. ET-rapport genereres sammen med kontrollkort.
                </p>
              )}
              {!canChoose48 && (
                <p className="mt-1.5 text-xs text-slate-400">
                  48-måneder krever ET-kvalifikasjon for utførende inspektør.
                </p>
              )}
              {cameFromListe && selectedCount > 1 && (
                <p className="mt-1.5 text-xs text-slate-500">
                  {formData.status === 'OK'
                    ? `Kontrollkort genereres for ${selectedCount} utstyr ved publisering.`
                    : 'Kun dette utstyret får kontrollkort når status ikke er OK.'}
                </p>
              )}
            </div>

            {/* Ordrenummer + Inspeksjonsdato */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5 h-[1.125rem]">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Ordrenummer</p>
                  {orderNoLocked && (
                    <button
                      type="button"
                      onClick={() => setOrderNoLocked(false)}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                      title="Lås opp for manuell redigering"
                    >
                      <Lock size={11} />
                      <span>Lås opp</span>
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.orderNo}
                    onChange={e => set('orderNo', e.target.value)}
                    readOnly={orderNoLocked}
                    placeholder="f.eks. I2603008"
                    className={`${selectCls} pr-9 ${orderNoLocked ? 'bg-slate-50 text-slate-500 cursor-default' : ''}`}
                    style={{ minHeight: 48 }}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {orderNoLocked ? (
                      <Lock size={14} className="text-slate-300" />
                    ) : orderNoValid ? (
                      <CheckCircle size={14} className="text-emerald-500" />
                    ) : formData.orderNo.length > 5 ? (
                      <AlertCircle size={14} className="text-amber-400" />
                    ) : null}
                  </span>
                </div>
                {!orderNoLocked && !orderNoValid && formData.orderNo.length > 5 && (
                  <p className="mt-1 text-xs text-amber-600">
                    Format: <span className="font-mono">IYYMMXXX</span> — f.eks. <span className="font-mono">I2603008</span>
                  </p>
                )}
              </div>

              <div>
                {fieldLabel('Inspeksjonsdato')}
                <input
                  type="date"
                  value={formData.performedDate}
                  onChange={e => set('performedDate', e.target.value)}
                  className={selectCls}
                  style={{ minHeight: 48 }}
                />
              </div>
            </div>

            {/* Kontrollert iht */}
            <div>
              {fieldLabel('Kontrollert iht')}
              <div className="flex gap-2">
                {CONTROLLED_OPTIONS.map(option => {
                  const checked = formData.controlledAgainst === option.value
                  return (
                    <label
                      key={option.id}
                      className={`flex-1 flex items-center gap-2.5 px-3 py-2.5 border rounded-md cursor-pointer transition-colors ${
                        checked
                          ? 'border-[#1E3A5F] bg-[#1E3A5F]/5'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      style={{ minHeight: 44 }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => set('controlledAgainst', checked ? '' : option.value)}
                        className="w-5 h-5 rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F] shrink-0"
                      />
                      <span className="text-sm font-semibold text-slate-800 leading-tight">
                        {option.label}
                      </span>
                    </label>
                  )
                })}
              </div>
              {!controlledAgainstValid && (
                <p className="mt-1 text-xs text-amber-600">Velg minst ett alternativ for å fortsette.</p>
              )}
            </div>

            {/* Status */}
            <div>
              {fieldLabel('Status')}
              <div className="flex gap-1.5">
                {STATUS_OPTIONS.map(o => {
                  const selected = formData.status === o.value
                  return (
                    <button
                      key={o.value}
                      type="button"
                      title={o.label}
                      onClick={() => selectStatus(o.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                        selected
                          ? `${o.color} text-white border-transparent`
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {!selected && <span className={`w-1.5 h-1.5 rounded-full ${o.color}`} />}
                      {o.value}
                    </button>
                  )
                })}
              </div>
              {ndtMoDefault && !month48 && (
                <p className="mt-1.5 text-xs text-slate-500">
                  NDT utløper før neste årlige kontroll, så MO er foreslått. Endre status ved behov.
                </p>
              )}
            </div>

            {/* Merknad — only if not OK */}
            {formData.status !== 'OK' && (
              <div>
                {fieldLabel(formData.status === 'C' ? 'Kommentar' : 'Merknad')}
                <textarea
                  value={formData.remarks}
                  onChange={e => set('remarks', e.target.value)}
                  placeholder={formData.status === 'C' ? 'Kommentar...' : 'Beskriv avviket...'}
                  rows={3}
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800 resize-none transition-colors"
                />
              </div>
            )}

            {/* Utført av + Ordreansvarlig */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                {fieldLabel('Utført av')}
                {loadingInspectors && !formData.performedById ? (
                  <div className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-400 border border-slate-200 rounded-md bg-slate-50" style={{ minHeight: 48 }}>
                    <Loader2 size={14} className="animate-spin shrink-0" />
                    Laster...
                  </div>
                ) : (
                  <select
                    value={formData.performedById}
                    onChange={e => set('performedById', e.target.value)}
                    disabled={!canChoosePerformer}
                    className={`${selectCls} ${!canChoosePerformer ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                    style={{ minHeight: 48 }}
                  >
                    {canChoosePerformer && <option value="">Velg inspektør...</option>}
                    {performerOptions.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.full_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                {fieldLabel('Ordreansvarlig')}
                {loadingSupervisors ? (
                  <div className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-400 border border-slate-200 rounded-md bg-slate-50" style={{ minHeight: 48 }}>
                    <Loader2 size={14} className="animate-spin shrink-0" />
                    Laster...
                  </div>
                ) : (
                  <select
                    value={formData.ordreansvarligId}
                    onChange={e => set('ordreansvarligId', e.target.value)}
                    className={selectCls}
                    style={{ minHeight: 48 }}
                  >
                    <option value="">Velg ordreansvarlig...</option>
                    {supervisors.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.full_name} {s.role === 'admin' ? '(Admin)' : '(Tilsynsansvarlig)'}
                      </option>
                    ))}
                  </select>
                )}
                {selectedOrdreansvarlig && !selectedOrdreansvarlig.signature && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600">
                    <AlertCircle size={12} className="shrink-0" />
                    Valgt ordreansvarlig har ikke registrert signatur
                  </p>
                )}
              </div>
            </div>

            {/* Vedlegg */}
            <div>
              {fieldLabel('Vedlegg (valgfritt)')}
              {!uploadedImage ? (
                <label
                  htmlFor="inspection-image-upload"
                  className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 rounded-md py-7 cursor-pointer hover:border-[#1E3A5F] hover:bg-slate-50 transition-colors"
                >
                  <input
                    id="inspection-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Upload size={20} className="text-slate-300" />
                  <p className="text-sm text-slate-400">Last opp bilde</p>
                  <p className="text-xs text-slate-300">JPG, PNG opptil 10 MB</p>
                </label>
              ) : (
                <div className="relative border border-slate-200 rounded-md overflow-hidden">
                  <img
                    src={uploadedImage}
                    alt="Vedlegg"
                    className="w-full h-auto max-h-56 object-contain bg-slate-50"
                  />
                  <button
                    onClick={() => setUploadedImage(null)}
                    className="absolute top-2 right-2 p-1.5 bg-white border border-slate-200 text-slate-500 rounded-md hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors shadow-sm"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                <AlertCircle size={14} className="shrink-0" />
                {error}
              </div>
            )}
          </div>

          {/* ── Footer ─────────────────────────────────────── */}
          <div className="border-t border-slate-200 px-5 py-4 flex items-center gap-3 bg-slate-50 shrink-0">
            <button
              onClick={handlePreview}
              disabled={isGenerating || isPublishing || !!previewProgress || !formData.performedById || !formData.ordreansvarligId || !formData.performedDate || !controlledAgainstValid}
              className="flex-1 flex items-center justify-center gap-2 px-4 bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-md hover:bg-slate-100 transition-colors disabled:opacity-40"
              style={{ minHeight: 48 }}
            >
              {isGenerating || previewProgress
                ? <><Loader2 size={15} className="animate-spin" /><span>{previewProgress || 'Forhåndsviser...'}</span></>
                : <><Eye size={15} />Forhåndsvis</>}
            </button>
            <button
              onClick={handlePublish}
              disabled={!canPublish}
              className="flex-1 flex items-center justify-center gap-2 px-4 bg-[#1E3A5F] text-white text-sm font-semibold rounded-md hover:bg-[#152C4A] transition-colors shadow-sm disabled:opacity-40"
              style={{ minHeight: 48 }}
            >
              {isGenerating || isPublishing
                ? <><Loader2 size={15} className="animate-spin" /><span>{publishProgress || 'Publiserer...'}</span></>
                : <><ClipboardList size={15} /><span>{bulkCount > 1 ? `Publiser ${bulkCount}` : 'Publiser'}</span></>}
            </button>
          </div>
          </>
          )}

        </div>
      </div>

      {/* ── PDF Preview overlay ─────────────────────────── */}
      {showPreview && previewUrl && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-white">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-white shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <p className="text-base font-semibold text-slate-800 truncate min-w-0">
                Forhåndsvisning
                <span className="text-slate-300 mx-2">·</span>
                <span className="text-slate-400 font-normal">
                  {previewCount > 1
                    ? `${previewCount} kontrollkort`
                    : (equipment.serial_number || equipment.asset_number)}
                </span>
              </p>
              {month48 && (
                <div className="flex items-center rounded-md border border-slate-200 overflow-hidden shrink-0">
                  <button
                    onClick={() => setPreviewTab('control_card')}
                    className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                      previewTab === 'control_card'
                        ? 'bg-[#1E3A5F] text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Kontrollkort
                  </button>
                  <button
                    onClick={() => setPreviewTab('et')}
                    className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                      previewTab === 'et'
                        ? 'bg-[#1E3A5F] text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    ET-rapport
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handlePublish}
                disabled={!canPublish}
                className="flex items-center justify-center gap-1.5 px-4 text-sm font-semibold text-white bg-[#1E3A5F] rounded-md hover:bg-[#152C4A] transition-colors disabled:opacity-40"
                style={{ minHeight: 48 }}
              >
                {isGenerating || isPublishing
                  ? <><Loader2 size={15} className="animate-spin" /><span>Publiserer...</span></>
                  : 'Publiser'}
              </button>
              <button
                onClick={handleClosePreview}
                className="flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                style={{ minHeight: 48, minWidth: 48 }}
                aria-label="Lukk forhåndsvisning"
              >
                <X size={22} />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 bg-slate-200">
            {previewTab === 'et' && !etPreviewUrl ? (
              <div className="w-full h-full flex items-center justify-center gap-2 text-slate-500">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm font-medium">Genererer ET-rapport...</span>
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="w-full h-full flex items-center justify-center gap-2 text-slate-500">
                    <Loader2 size={18} className="animate-spin" />
                    <span className="text-sm font-medium">Laster forhåndsvisning...</span>
                  </div>
                }
              >
                <PdfPreview
                  src={previewTab === 'et' && etPreviewUrl ? etPreviewUrl : previewUrl}
                  title={previewTab === 'et' ? 'ET-rapport' : 'Kontrollkort'}
                />
              </Suspense>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default InspectionModal
